import { z } from 'zod';
import {
  KnowledgeItemType,
  KnowledgeSource,
  AiExtractionStatus,
  UnclassifiedEmailStatus,
} from '../enums';

export { KnowledgeItemType, KnowledgeSource, AiExtractionStatus, UnclassifiedEmailStatus };

// ── Enum Schemas ─────────────────────────────────────────────────────────────

export const KnowledgeItemTypeSchema = z.nativeEnum(KnowledgeItemType);
export const KnowledgeSourceSchema = z.nativeEnum(KnowledgeSource);
export const AiExtractionStatusSchema = z.nativeEnum(AiExtractionStatus);
export const UnclassifiedEmailStatusSchema = z.nativeEnum(UnclassifiedEmailStatus);

// ── Email metadata (JSONB shape for type=EMAIL) ─────────────────────────────

export const EmailMetadataSchema = z.object({
  thread_id: z.string().uuid().optional(),
  external_message_id: z.string().optional(),
  sender: z.object({
    name: z.string(),
    email: z.string().email(),
  }).optional(),
  recipients: z.array(z.object({
    name: z.string(),
    email: z.string().email(),
    type: z.enum(['to', 'cc', 'bcc']),
  })).optional(),
  body_text: z.string().optional(),
  body_html_path: z.string().optional(),
  received_at: z.string().optional(),
  has_attachments: z.boolean().optional(),
  attachment_ids: z.array(z.string().uuid()).optional(),
});

// ── Knowledge Items ─────────────────────────────────────────────────────────

export const KnowledgeItemSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  type: KnowledgeItemTypeSchema,
  title: z.string(),
  source: KnowledgeSourceSchema,
  storage_path: z.string().nullable(),
  url: z.string().nullable(),
  metadata: z.record(z.unknown()).default({}),
  file_size: z.number().nullable(),
  mime_type: z.string().nullable(),
  created_by: z.string().uuid().nullable(),
  folder_id: z.string().uuid().nullable().optional(),
  processing_status: z.enum(['processing', 'ready', 'failed']).nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateKnowledgeItemSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  type: KnowledgeItemTypeSchema,
  url: z.string().url().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const KnowledgeItemQuerySchema = z.object({
  type: KnowledgeItemTypeSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

// ── Email Threads ───────────────────────────────────────────────────────────

export const EmailThreadSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  subject: z.string(),
  external_thread_id: z.string().nullable(),
  participants: z.array(z.object({
    name: z.string(),
    email: z.string(),
    role: z.string().optional(),
  })).default([]),
  last_message_at: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const EmailThreadWithMessagesSchema = EmailThreadSchema.extend({
  messages: z.array(KnowledgeItemSchema),
});

// ── AI Extractions ──────────────────────────────────────────────────────────

export const AiExtractionSchema = z.object({
  id: z.string().uuid(),
  knowledge_item_id: z.string().uuid(),
  field_name: z.string(),
  ai_value: z.string().nullable(),
  ai_confidence: z.number().nullable(),
  override_value: z.string().nullable(),
  target_table: z.string().nullable(),
  target_field: z.string().nullable(),
  status: AiExtractionStatusSchema,
  created_at: z.string().datetime(),
});

export const UpdateAiExtractionSchema = z.object({
  override_value: z.string().nullable().optional(),
  status: AiExtractionStatusSchema,
});

// ── Unclassified Emails ─────────────────────────────────────────────────────

export const UnclassifiedEmailSchema = z.object({
  id: z.string().uuid(),
  external_message_id: z.string().nullable(),
  subject: z.string().nullable(),
  sender: z.object({
    name: z.string(),
    email: z.string(),
  }).nullable(),
  body_preview: z.string().nullable(),
  received_at: z.string().nullable(),
  raw_data: z.record(z.unknown()).nullable(),
  ai_suggested_project_id: z.string().uuid().nullable(),
  ai_confidence: z.number().nullable(),
  status: UnclassifiedEmailStatusSchema,
  classified_project_id: z.string().uuid().nullable(),
  classified_by: z.string().uuid().nullable(),
  classified_at: z.string().nullable(),
  created_at: z.string().datetime(),
});

export const ClassifyEmailSchema = z.object({
  project_id: z.string().uuid(),
});

// ── OpenClaw Agent Schemas ──────────────────────────────────────────────────

export const IngestEmailSchema = z.object({
  external_message_id: z.string(),
  external_thread_id: z.string().optional(),
  subject: z.string(),
  sender: z.object({ name: z.string(), email: z.string() }),
  recipients: z.array(z.object({
    name: z.string(),
    email: z.string(),
    type: z.enum(['to', 'cc', 'bcc']),
  })),
  body_text: z.string(),
  body_html: z.string().optional(),
  received_at: z.string(),
  attachments: z.array(z.object({
    filename: z.string(),
    content_base64: z.string(),
    mime_type: z.string(),
  })).optional(),
  reference_code_match: z.string().optional(),
  ai_confidence: z.number().min(0).max(1),
});

export const CreateExtractionsSchema = z.object({
  extractions: z.array(z.object({
    field_name: z.string(),
    ai_value: z.string(),
    ai_confidence: z.number().min(0).max(1),
    target_table: z.string(),
    target_field: z.string(),
  })),
});

// ── Move Knowledge Item ────────────────────────────────────────────────────────

export const MoveKnowledgeItemSchema = z.object({
  folder_id: z.string().uuid().nullable(),
});

// ── Type exports ────────────────────────────────────────────────────────────

export type KnowledgeItemTypeEnum = z.infer<typeof KnowledgeItemTypeSchema>;
export type KnowledgeSourceEnum = z.infer<typeof KnowledgeSourceSchema>;
export type AiExtractionStatusEnum = z.infer<typeof AiExtractionStatusSchema>;
export type UnclassifiedEmailStatusEnum = z.infer<typeof UnclassifiedEmailStatusSchema>;
export type EmailMetadata = z.infer<typeof EmailMetadataSchema>;
export type KnowledgeItem = z.infer<typeof KnowledgeItemSchema>;
export type CreateKnowledgeItem = z.infer<typeof CreateKnowledgeItemSchema>;
export type KnowledgeItemQuery = z.infer<typeof KnowledgeItemQuerySchema>;
export type EmailThread = z.infer<typeof EmailThreadSchema>;
export type EmailThreadWithMessages = z.infer<typeof EmailThreadWithMessagesSchema>;
export type AiExtraction = z.infer<typeof AiExtractionSchema>;
export type UpdateAiExtraction = z.infer<typeof UpdateAiExtractionSchema>;
export type UnclassifiedEmail = z.infer<typeof UnclassifiedEmailSchema>;
export type ClassifyEmail = z.infer<typeof ClassifyEmailSchema>;
export type IngestEmail = z.infer<typeof IngestEmailSchema>;
export type CreateExtractions = z.infer<typeof CreateExtractionsSchema>;
export type MoveKnowledgeItem = z.infer<typeof MoveKnowledgeItemSchema>;
